import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { getLanguage } from '../helpers/fetcher';
import { referenceDataSyncService } from '../services/referenceDataSyncService';
import { indexedDBManager } from './indexedDB';

/**
 * Debug utility for troubleshooting reference data sync issues
 */
export class DebugReferenceSync {
  static async checkSyncHealth(): Promise<void> {
    console.group('🔍 Reference Data Sync Health Check');

    try {
      // Check online status
      console.log(`🌐 Online status: ${navigator.onLine}`);

      // Check sync status
      const syncStatus = await referenceDataSyncService.getSyncStatus();
      console.log('📊 Sync Status:', syncStatus);

      // Check cached data
      const establishments = await indexedDBManager.getReferenceData('establishments');
      const locations = await indexedDBManager.getReferenceData('locations');
      const productCategories = await indexedDBManager.getReferenceData('productCategories');

      console.log(`🏢 Cached establishments: ${establishments.length}`);
      console.log(`📍 Cached locations: ${locations.length}`);
      console.log(`📂 Cached categories: ${productCategories.length}`);

      // Check sync maps
      const syncMaps = await indexedDBManager.getAllSyncMaps();
      console.log('🗺️ Sync Maps:', syncMaps);

      // Check storage size
      const dbSize = await indexedDBManager.getDatabaseSize();
      console.log(`💾 Database size: ${(dbSize / 1024).toFixed(2)} KB`);
    } catch (error) {
      console.error('❌ Health check failed:', error);
    }

    console.groupEnd();
  }

  static async testEntitySync(entity: string): Promise<void> {
    console.group(`🧪 Testing ${entity} sync`);

    try {
      console.log(`⏰ Starting ${entity} sync test...`);
      const result = await referenceDataSyncService.forceSyncEntity(entity);
      console.log(`✅ ${entity} sync result:`, result);
    } catch (error) {
      console.error(`❌ ${entity} sync failed:`, error);

      // Additional debugging info
      if (error instanceof Error) {
        console.log('🔍 Error details:');
        console.log('- Message:', error.message);
        console.log('- Stack:', error.stack);
      }
    }

    console.groupEnd();
  }

  static async testAllEntities(): Promise<void> {
    console.group('🧪 Testing all entity syncs');

    const entities = ['establishments', 'locations', 'productCategories'];

    for (const entity of entities) {
      await this.testEntitySync(entity);
    }

    console.groupEnd();
  }

  static async checkApiEndpoints(): Promise<void> {
    console.group('🌐 Testing API endpoints');

    const baseUrl = 'http://localhost:3000';
    const endpoints = [
      `/${API_ENDPOINTS.ESTABLISHMENT.GET_ESTABLISHMENTS_METADATA}`,
      `/${API_ENDPOINTS.LOCATION.GET_LOCATIONS_METADATA}`,
      `/${API_ENDPOINTS.PRODUCT_CATEGORY.GET_PRODUCT_CATEGORIES_METADATA}`,
      `/${API_ENDPOINTS.ESTABLISHMENT.ESTABLISHMENTS}`,
      `/${API_ENDPOINTS.LOCATION.LOCATIONS}`,
      `/${API_ENDPOINTS.PRODUCT_CATEGORY.PRODUCT_CATEGORIES}`,
    ];

    const authToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': getLanguage(),
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    for (const endpoint of endpoints) {
      try {
        console.log(`🔗 Testing: ${baseUrl}${endpoint}`);
        const response = await fetch(`${baseUrl}${endpoint}`, {
          method: 'GET',
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ ${endpoint} - Status: ${response.status}`, data);
        } else {
          const errorText = await response.text();
          console.error(`❌ ${endpoint} - Status: ${response.status}, Error: ${errorText}`);
        }
      } catch (error) {
        console.error(`❌ ${endpoint} - Network error:`, error);
      }
    }

    console.groupEnd();
  }

  static async clearAllData(): Promise<void> {
    console.group('🗑️ Clearing all reference data');

    try {
      await referenceDataSyncService.clearAllData();
      console.log('✅ All reference data cleared');
    } catch (error) {
      console.error('❌ Failed to clear data:', error);
    }

    console.groupEnd();
  }

  static async fullDiagnostic(): Promise<void> {
    console.log('🚀 Starting full reference data sync diagnostic...');

    await this.checkSyncHealth();
    await this.checkApiEndpoints();

    if (navigator.onLine) {
      await this.testAllEntities();
    } else {
      console.log('⚠️ Skipping sync tests - offline');
    }

    console.log('🏁 Diagnostic complete');
  }
}

// Make it available globally for debugging
(window as any).debugReferenceSync = DebugReferenceSync;


